import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

export default function StoreReturns() {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusTab, setStatusTab] = useState('issued'); // 'issued' (awaiting return), 'returned' (returned/approved), 'all'

  const [activeItem, setActiveItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    status: 'returned',
    remarks: '',
    deductionAmount: 0
  });

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const statusQuery = statusTab === 'all' ? '' : `?status=${statusTab}`;
      const res = await axios.get(`/api/v1/admin/store/returns${statusQuery}`, { headers });
      if (res.data.success) setReturns(res.data.data);
    } catch (err) {
      setError('Failed to fetch returns.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReturns();
  }, [statusTab]);

  const handleOpenProcess = (item) => {
    setActiveItem(item);
    setForm({
      status: 'returned',
      remarks: '',
      deductionAmount: 0
    });
    setDialogOpen(true);
  };

  const handleProcessReturn = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(`/api/v1/admin/store/returns/${activeItem.id}/approve`, {
        status: form.status,
        remarks: form.remarks
      }, { headers });

      setDialogOpen(false);
      fetchReturns();
    } catch (err) {
      alert('Failed to process return approval');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AssignmentReturnIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">Rental Returns & Deposit Approvals</Typography>
          <Typography variant="body2" color="text.secondary">
            Process returned campsite gear, inspect equipment condition, and approve refundable security deposits.
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={statusTab} onChange={(e, val) => setStatusTab(val)} textColor="primary" indicatorColor="primary">
          <Tab label="Awaiting Return (Issued Gear)" value="issued" />
          <Tab label="Returned & Approved" value="returned" />
          <Tab label="Damaged / Lost Claims" value="damaged" />
          <Tab label="All Rental History" value="all" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead sx={{ bgcolor: '#F8FAFC' }}>
              <TableRow>
                <TableCell>Participant / Camper</TableCell>
                <TableCell>Camp & Batch</TableCell>
                <TableCell>Rental Item</TableCell>
                <TableCell>Deposit Amount</TableCell>
                <TableCell>Issue Date</TableCell>
                <TableCell>Return Status</TableCell>
                <TableCell>Inspection Remarks</TableCell>
                <TableCell align="right">Approval Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {returns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No rental items found for this view.</TableCell>
                </TableRow>
              ) : (
                returns.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">{item.order?.customerName}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.order?.customerPhone}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.order?.registration?.trek?.name || 'Trek Basecamp'}</Typography>
                      <Typography variant="caption" color="text.secondary">Order: {item.order?.orderNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      <strong>{item.productName}</strong>
                      {item.variantName && <Typography variant="caption" display="block">({item.variantName})</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="success.main" fontWeight="bold">
                        ₹{Number(item.unitDeposit * item.quantity).toLocaleString('en-IN')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.issuedAt ? new Date(item.issuedAt).toLocaleDateString('en-IN') : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.rentalStatus.toUpperCase()}
                        size="small"
                        color={
                          item.rentalStatus === 'returned' ? 'success' :
                          item.rentalStatus === 'issued' ? 'warning' : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {item.remarks || 'No remarks'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {item.rentalStatus === 'issued' ? (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<ThumbUpAltIcon />}
                          onClick={() => handleOpenProcess(item)}
                        >
                          Process Return
                        </Button>
                      ) : (
                        <Chip label="Processed" size="small" variant="outlined" color="success" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Return Approval Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Inspect & Approve Rental Return</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="subtitle1">
              <strong>Item:</strong> {activeItem?.productName} ({activeItem?.variantName || 'Standard'})
            </Typography>
            <Typography variant="body2" color="success.main">
              <strong>Collected Deposit to Refund:</strong> ₹{activeItem?.unitDeposit * activeItem?.quantity}
            </Typography>

            <TextField
              select
              label="Return Condition Assessment"
              fullWidth
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <MenuItem value="returned">✓ Good Condition (Approve Full Deposit Refund & Restock)</MenuItem>
              <MenuItem value="damaged">⚠️ Damaged Gear (Deduct Deposit / Partial Refund)</MenuItem>
              <MenuItem value="lost">❌ Lost / Not Returned (Forfeit Full Deposit)</MenuItem>
            </TextField>

            <TextField
              label="Inspection Remarks & Condition Notes"
              fullWidth
              multiline
              rows={3}
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              placeholder="e.g. Gear inspected by campsite instructor, zero defects, returned cleanly."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleProcessReturn}>
            Confirm Approval & Restock
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
